import type { AccountConnection } from "@/lib/amazon-api";

/**
 * Shop-scope domain model: one Amazon Ads connection is one seller account the
 * user recognises by name; each of its Shops is one marketplace of that account.
 * Everything here is pure so the trigger summary and the popover tree read the
 * same derived shape.
 */

export type ScopeRegion = "na" | "eu" | "fe";

export interface ScopeShop {
  shopId: string;
  name: string;
  /** Owning account (connection) id; empty for store-only rows. */
  accountId: string;
  /** Two-letter marketplace code; empty while account metadata is unavailable. */
  marketplace: string;
  domain: string;
  profileId: string;
  /** Ads authorization is broken — the Shop can't be newly selected until reconnected. */
  needsReconnect: boolean;
}

export interface ScopeAccount {
  id: string;
  name: string;
  /** The Amazon login (usually an email) — searchable, not displayed. */
  label: string | null;
  accountType: "seller" | "vendor" | null;
  regions: ScopeRegion[];
  /** Every Shop of the account needs reconnecting (the authorization itself expired). */
  needsReconnect: boolean;
  shops: ScopeShop[];
}

export interface ScopeTree {
  accounts: ScopeAccount[];
  /** Workspace Shops without account metadata (accounts query loading or failed). */
  ungrouped: ScopeShop[];
  /** Every Shop of the scope universe, in display order. */
  shops: ScopeShop[];
}

/** Active Shops of the active Workspace, grouped by Amazon Ads connection. */
export function filterSwitcherConnections(
  connections: AccountConnection[],
  workspaceId: string | null,
): AccountConnection[] {
  return connections
    .map((connection) => ({
      ...connection,
      shops: connection.shops.filter(
        (shop) => shop.isActive && (!workspaceId || shop.workspaceId === workspaceId),
      ),
    }))
    .filter((connection) => connection.shops.length > 0);
}

export function marketplaceCode(marketplaceName: string | undefined): string {
  const normalized = marketplaceName?.trim();
  if (!normalized) return "AD";

  const countryCode = normalized.toUpperCase();
  if (countryCode === "GB") return "UK";
  if (/^[A-Z]{2}$/.test(countryCode)) return countryCode;

  const name = normalized.toLowerCase();
  if (name.includes("co.uk")) return "UK";
  if (name.includes(".de")) return "DE";
  if (name.includes(".fr")) return "FR";
  if (name.includes(".it")) return "IT";
  if (name.includes(".es")) return "ES";
  if (name.includes(".ca")) return "CA";
  if (name.includes(".com.mx")) return "MX";
  if (name.includes(".com.br")) return "BR";
  if (name.includes(".co.jp")) return "JP";
  if (name.includes(".com.au")) return "AU";
  return "US";
}

/** "US" → 🇺🇸 via regional indicators; UK is Amazon's alias for GB. */
export function flagEmoji(countryCode: string): string {
  const cc = countryCode === "UK" ? "GB" : countryCode;
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  return String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + (ch.charCodeAt(0) - 65)));
}

const MARKETPLACES: Record<string, { domain: string; region: ScopeRegion }> = {
  US: { domain: "amazon.com", region: "na" },
  CA: { domain: "amazon.ca", region: "na" },
  MX: { domain: "amazon.com.mx", region: "na" },
  BR: { domain: "amazon.com.br", region: "na" },
  UK: { domain: "amazon.co.uk", region: "eu" },
  DE: { domain: "amazon.de", region: "eu" },
  FR: { domain: "amazon.fr", region: "eu" },
  IT: { domain: "amazon.it", region: "eu" },
  ES: { domain: "amazon.es", region: "eu" },
  NL: { domain: "amazon.nl", region: "eu" },
  SE: { domain: "amazon.se", region: "eu" },
  PL: { domain: "amazon.pl", region: "eu" },
  BE: { domain: "amazon.com.be", region: "eu" },
  IE: { domain: "amazon.ie", region: "eu" },
  TR: { domain: "amazon.com.tr", region: "eu" },
  AE: { domain: "amazon.ae", region: "eu" },
  SA: { domain: "amazon.sa", region: "eu" },
  EG: { domain: "amazon.eg", region: "eu" },
  IN: { domain: "amazon.in", region: "eu" },
  ZA: { domain: "amazon.co.za", region: "eu" },
  JP: { domain: "amazon.co.jp", region: "fe" },
  AU: { domain: "amazon.com.au", region: "fe" },
  SG: { domain: "amazon.sg", region: "fe" },
};

export const KNOWN_MARKETPLACE_CODES = Object.keys(MARKETPLACES);

export function marketplaceDomain(code: string): string {
  return MARKETPLACES[code]?.domain ?? "";
}

/** Localised country name for a marketplace code ("DE" → "Germany" / "德国"). */
export function marketplaceCountryName(code: string, language: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return code;
  try {
    return (
      new Intl.DisplayNames([language], { type: "region" }).of(code === "UK" ? "GB" : code) ?? code
    );
  } catch {
    return code;
  }
}

function needsReconnect(connectionStatus: string, shopStatus: string): boolean {
  return connectionStatus !== "connected" || shopStatus !== "connected";
}

/**
 * Builds the account → marketplace tree for the workspace scope universe.
 * Connections must already be workspace-filtered; store Shops the accounts
 * response doesn't cover (still loading, failed, or not yet listed) fall into
 * `ungrouped` so the selector never hides a selectable Shop.
 */
export function buildScopeTree(
  connections: AccountConnection[],
  workspaceShops: ReadonlyArray<{ shopId: string; shopName: string }>,
): ScopeTree {
  const universe = new Set(workspaceShops.map((shop) => shop.shopId));
  const accounts: ScopeAccount[] = [];
  for (const connection of connections) {
    const shops: ScopeShop[] = connection.shops
      .filter((shop) => universe.has(shop.shopId))
      .map((shop) => {
        const code = marketplaceCode(shop.marketplaceName);
        return {
          shopId: shop.shopId,
          name: shop.shopName,
          accountId: connection.id,
          marketplace: code,
          domain: marketplaceDomain(code),
          profileId: shop.profileId,
          needsReconnect: needsReconnect(connection.status, shop.status),
        };
      });
    const first = shops[0];
    if (!first) continue;
    // A connection's Shops share one display name (renames apply to the whole
    // group); only diverging legacy names fall back to the Amazon login label.
    const names = new Set(shops.map((shop) => shop.name));
    const regions = [
      ...new Set(
        shops.map((shop) => MARKETPLACES[shop.marketplace]?.region).filter((r) => r !== undefined),
      ),
    ];
    accounts.push({
      id: connection.id,
      name: names.size === 1 ? first.name : (connection.label ?? first.name),
      label: connection.label,
      accountType: connection.shops.find((shop) => shop.accountType)?.accountType ?? null,
      regions,
      needsReconnect: shops.every((shop) => shop.needsReconnect),
      shops,
    });
  }
  const grouped = new Set(accounts.flatMap((account) => account.shops.map((shop) => shop.shopId)));
  const ungrouped: ScopeShop[] = workspaceShops
    .filter((shop) => !grouped.has(shop.shopId))
    .map((shop) => ({
      shopId: shop.shopId,
      name: shop.shopName,
      accountId: "",
      marketplace: "",
      domain: "",
      profileId: "",
      needsReconnect: false,
    }));
  return {
    accounts,
    ungrouped,
    shops: [...accounts.flatMap((account) => account.shops), ...ungrouped],
  };
}

export interface FilteredAccount {
  account: ScopeAccount;
  /** Shops shown under the account: all of them, or only the matches. */
  shops: ScopeShop[];
  /** The account itself matched, so every marketplace stays visible. */
  accountMatched: boolean;
}

export interface FilteredTree {
  accounts: FilteredAccount[];
  ungrouped: ScopeShop[];
  matchCount: number;
}

/**
 * Search keeps parents as context: a matching account shows all its
 * marketplaces; otherwise an account shows only the marketplaces that match.
 */
export function filterScopeTree(
  tree: ScopeTree,
  query: string,
  countryName: (code: string) => string,
): FilteredTree {
  const q = query.trim().toLowerCase();
  if (!q) {
    return {
      accounts: tree.accounts.map((account) => ({
        account,
        shops: account.shops,
        accountMatched: true,
      })),
      ungrouped: tree.ungrouped,
      matchCount: tree.shops.length,
    };
  }
  const has = (value: string | null | undefined) => (value ?? "").toLowerCase().includes(q);
  const shopMatches = (shop: ScopeShop) =>
    has(shop.name) ||
    has(shop.profileId) ||
    has(shop.marketplace) ||
    has(shop.domain) ||
    (shop.marketplace !== "" && has(countryName(shop.marketplace)));

  const accounts: FilteredAccount[] = [];
  for (const account of tree.accounts) {
    if (has(account.name) || has(account.label)) {
      accounts.push({ account, shops: account.shops, accountMatched: true });
      continue;
    }
    const shops = account.shops.filter(shopMatches);
    if (shops.length > 0) accounts.push({ account, shops, accountMatched: false });
  }
  const ungrouped = tree.ungrouped.filter(shopMatches);
  return {
    accounts,
    ungrouped,
    matchCount: accounts.reduce((sum, entry) => sum + entry.shops.length, 0) + ungrouped.length,
  };
}

/** A known marketplace the query names that this workspace hasn't connected. */
export function unconnectedMarketplaceFor(
  tree: ScopeTree,
  query: string,
  countryName: (code: string) => string,
): string | null {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return null;
  const connected = new Set(tree.shops.map((shop) => shop.marketplace));
  return (
    KNOWN_MARKETPLACE_CODES.find(
      (code) =>
        !connected.has(code) &&
        (code.toLowerCase() === q ||
          countryName(code).toLowerCase().includes(q) ||
          marketplaceDomain(code) === q),
    ) ?? null
  );
}

export type CheckState = "checked" | "indeterminate" | "unchecked";

/**
 * Tri-state over the Shops a parent controls. Reconnect-blocked Shops are only
 * counted when the parent has nothing selectable, so a broken marketplace never
 * keeps "All shops" or its account stuck at indeterminate.
 */
export function checkStateOf(shops: readonly ScopeShop[], draft: ReadonlySet<string>): CheckState {
  const selectable = shops.filter((shop) => !shop.needsReconnect);
  const considered = selectable.length > 0 ? selectable : shops;
  const selected = considered.filter((shop) => draft.has(shop.shopId)).length;
  if (selected === 0) return "unchecked";
  return selected === considered.length ? "checked" : "indeterminate";
}

/** Checking a parent adds its selectable Shops; unchecking removes them. */
export function toggleShops(shops: readonly ScopeShop[], draft: ReadonlySet<string>): Set<string> {
  const next = new Set(draft);
  const selectable = shops.filter((shop) => !shop.needsReconnect);
  const on = checkStateOf(shops, draft) !== "checked";
  for (const shop of selectable) {
    if (on) next.add(shop.shopId);
    else next.delete(shop.shopId);
  }
  return next;
}

export type ScopeSummary =
  | { kind: "all"; count: number }
  | { kind: "account"; name: string; count: number }
  | { kind: "shop"; name: string; marketplace: string }
  | { kind: "mix"; marketplaces: string[]; count: number }
  | { kind: "none" };

/** How the trigger summarises the applied scope (Paper "Trigger summarises the scope"). */
export function summarizeScope(
  tree: ScopeTree,
  scopeShopIds: readonly string[],
  isAll: boolean,
): ScopeSummary {
  if (isAll) return { kind: "all", count: tree.shops.length };
  const scope = new Set(scopeShopIds);
  const scoped = tree.shops.filter((shop) => scope.has(shop.shopId));
  const only = scoped[0];
  if (!only) return { kind: "none" };
  if (scoped.length === 1) return { kind: "shop", name: only.name, marketplace: only.marketplace };
  const account = tree.accounts.find(
    (candidate) =>
      candidate.shops.length === scoped.length &&
      candidate.shops.every((shop) => scope.has(shop.shopId)),
  );
  if (account) return { kind: "account", name: account.name, count: scoped.length };
  const marketplaces = [...new Set(scoped.map((shop) => shop.marketplace).filter(Boolean))];
  return { kind: "mix", marketplaces: marketplaces.slice(0, 3), count: scoped.length };
}

/** Accounts open on first paint: everything when the list is short, else the ones in play. */
export function initialExpandedAccounts(
  tree: ScopeTree,
  selected: ReadonlySet<string>,
  isAll: boolean,
): Set<string> {
  if (tree.shops.length <= 12 || tree.accounts.length === 1) {
    return new Set(tree.accounts.map((account) => account.id));
  }
  if (isAll) return new Set();
  return new Set(
    tree.accounts
      .filter((account) => account.shops.some((shop) => selected.has(shop.shopId)))
      .map((account) => account.id),
  );
}
